// Frontend mirror of the backend's `api/services/wet_signature_content.py` —
// a WET_SIGNATURE field's `content` is a JSON envelope
// (`{allowed_methods, method, value}`, design.md Decision 1 of
// `unify-wet-signature-field-type`). Every reader here tolerates
// missing/malformed content the same way the backend does: an invalid
// `allowed_methods` falls back to unrestricted, and a missing `method`/
// `value` is treated as empty rather than throwing.
import { parseJson } from "@components/FieldComponent/utils";

export type WetSignatureMethod = "typed" | "uploaded" | "drawn";

export const WET_SIGNATURE_METHODS: readonly WetSignatureMethod[] = ["typed", "uploaded", "drawn"];

export interface WetSignatureContent {
  allowedMethods: WetSignatureMethod[];
  method: WetSignatureMethod | null;
  value: string | null;
}

const isWetSignatureMethod = (value: unknown): value is WetSignatureMethod =>
  typeof value === "string" && (WET_SIGNATURE_METHODS as readonly string[]).includes(value);

export const parseWetSignatureContent = (content: string | null | undefined): WetSignatureContent => {
  const parsed = parseJson<Record<string, unknown>>(content);
  const data = parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};

  const rawAllowedMethods = data.allowed_methods;
  const allowedMethods: WetSignatureMethod[] =
    Array.isArray(rawAllowedMethods) && rawAllowedMethods.length > 0 && rawAllowedMethods.every(isWetSignatureMethod)
      ? (rawAllowedMethods as WetSignatureMethod[])
      : [...WET_SIGNATURE_METHODS];

  const method = isWetSignatureMethod(data.method) ? data.method : null;
  const value = typeof data.value === "string" ? data.value : null;

  return { allowedMethods, method, value };
};

export const serializeWetSignatureContent = (
  allowedMethods: WetSignatureMethod[],
  method?: WetSignatureMethod | null,
  value?: string | null,
): string => {
  const payload: Record<string, unknown> = { allowed_methods: allowedMethods };
  if (method != null) payload.method = method;
  if (value != null) payload.value = value;
  return JSON.stringify(payload);
};

export const isWetSignatureContentEmpty = (content: string | null | undefined): boolean => {
  const { value } = parseWetSignatureContent(content);
  return !value || value.trim() === "";
};
